import { useCallback, useState } from "react";
import type { Configuration } from "../../app/models/Configuration";
import { getConsent, saveConsent } from "./cookieConsentStorage";
import type { CookieConsent } from "./cookieConsentStorage";
import { resolveAnalyticsTags, initializeAnaliticsByFacility } from "../Analitics/analiticsConfigByFacility";
import type { ResolvedFacilityAnalytics } from "../Analitics/analiticsConfigByFacility";

export interface UseCookieConsentReturn {
  /** Whether the initial consent banner should be displayed. */
  shouldShowBanner: boolean;
  /** Current stored consent, null if not yet decided or expired. */
  consentValue: CookieConsent | null;
  /** True when at least one optional analytics/marketing tool is configured. */
  hasAnyOptionalTag: boolean;
  /** The resolved GA/GTM/Pixel IDs for the current facility. */
  resolvedTags: ResolvedFacilityAnalytics;
  /** Accept all cookies and initialize analytics immediately. */
  acceptAll: () => void;
  /** Reject optional cookies (necessary remain active). */
  rejectOptional: () => void;
  /** Save a specific optional consent value; initializes analytics if accepted. */
  updateConsent: (optional: boolean) => void;
}

export function useCookieConsent(facilityId: string, configuration: Configuration): UseCookieConsentReturn {
  const resolvedTags = resolveAnalyticsTags(configuration, facilityId);
  const hasAnyOptionalTag = !!(resolvedTags.pixelId || resolvedTags.gaId || resolvedTags.GTM);

  const [consentValue, setConsentValue] = useState<CookieConsent | null>(() =>
    facilityId ? getConsent(facilityId) : null,
  );

  const shouldShowBanner = !!configuration.cookieBarActive && hasAnyOptionalTag && consentValue === null;

  const acceptAll = useCallback(() => {
    saveConsent(facilityId, true);
    setConsentValue(getConsent(facilityId));
    initializeAnaliticsByFacility(facilityId, configuration);
  }, [facilityId, configuration]);

  const rejectOptional = useCallback(() => {
    saveConsent(facilityId, false);
    setConsentValue(getConsent(facilityId));
  }, [facilityId]);

  const updateConsent = useCallback(
    (optional: boolean) => {
      saveConsent(facilityId, optional);
      setConsentValue(getConsent(facilityId));
      if (optional) {
        initializeAnaliticsByFacility(facilityId, configuration);
      }
    },
    [facilityId, configuration],
  );

  return {
    shouldShowBanner,
    consentValue,
    hasAnyOptionalTag,
    resolvedTags,
    acceptAll,
    rejectOptional,
    updateConsent,
  };
}
